import styled from '@emotion/styled';
import { Button, TableCell, TableRow } from '@material-ui/core';
import { greens, greys, white } from '@/styles/colors';
import { sansSerif } from '@/styles/typography';

// Figma palette additions. These are page-local on purpose — promote them into
// styles/colors.js once the rest of the app adopts the new account surface.
const HEADER_GREY = '#8A8F98';
const AMBER = '#F5A623';

/* --------------------------------- the table -------------------------------- */

export const TableCard = styled.div`
    background: ${white.primary};
    border: 1px solid ${greys.border};
    border-radius: 10px;
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    min-height: 0;
    overflow: hidden;
`;

export const TableScroll = styled.div`
    flex-grow: 1;
    min-height: 0;
    overflow: auto;
`;

export const HeaderCell = styled(TableCell)`
    background: ${white.primary};
    border-bottom: 1px solid ${greys.border};
    color: ${HEADER_GREY};
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.7px;
    padding: 14px 16px;
    text-transform: uppercase;
    white-space: nowrap;
`;

export const BodyRow = styled(TableRow)`
    &:hover {
        background: ${white.secondary};
    }
`;

export const BodyCell = styled(TableCell)`
    border-bottom: 1px solid ${greys.border};
    color: ${greys.secondary};
    font-size: 14px;
    padding: 14px 16px;
    vertical-align: middle;
`;

export const ShipperName = styled.span`
    color: #0c0b1d;
    display: block;
    font-size: 15px;
    font-weight: 700;
    line-height: 1.3;
`;

export const ShipperMeta = styled.span`
    color: ${greys.subtitle};
    display: block;
    font-size: 12.5px;
    line-height: 1.5;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

export const RoutingGroup = styled.div`
    display: inline-flex;
    gap: 6px;
`;

export const RoutingChip = styled.button`
    ${sansSerif}
    align-items: center;
    background: ${({ selected }) => (selected ? greens.primary : white.primary)};
    border: 1px solid ${({ selected }) => (selected ? greens.primary : greys.border)};
    border-radius: 5px;
    color: ${({ selected }) => (selected ? white.primary : greys.secondary)};
    cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
    display: inline-flex;
    font-size: 12.5px;
    font-weight: 600;
    gap: 4px;
    opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};
    padding: 4px 9px;

    .MuiSvgIcon-root {
        color: ${({ selected }) => (selected ? white.primary : AMBER)};
        font-size: 14px;
    }

    &:hover {
        border-color: ${({ selected, disabled }) => (disabled || selected ? undefined : greys.primary)};
    }
`;

export const ShopIconWrap = styled.span`
    display: inline-flex;

    .MuiSvgIcon-root {
        color: ${({ selected }) => (selected ? white.primary : greys.primary)};
    }
`;

export const ProfilePlaceholder = styled.span`
    align-items: center;
    border: 1px dashed ${greys.subtitle};
    border-radius: 5px;
    color: ${greys.subtitle};
    display: flex;
    font-size: 13px;
    justify-content: center;
    padding: 5px 10px;
`;

export const ProfileValue = styled.span`
    color: ${greys.secondary};
    font-size: 13px;
    font-weight: 600;
`;

export const OpenButton = styled(Button)`
    ${sansSerif}
    border: 1px solid ${greys.border};
    border-radius: 6px;
    color: ${greys.secondary};
    font-size: 13px;
    font-weight: 700;
    min-width: 0;
    padding: 5px 20px;
    text-transform: none;
`;

// A field the Figma calls for that has no backing column yet. Renders nothing, but holds the
// box the value would have occupied so rows keep their height and columns keep their rhythm.
// Restoring a field is a matter of swapping this for the accessor — see SKIPPED_FIELDS in
// constants.js for the inventory and the values the design expects.
//
// `minHeight` only needs passing where the omitted element was taller than a line of text
// (a chip or a button); the default covers a plain value.
export const Skipped = styled.span`
    display: inline-block;
    min-height: ${({ minHeight }) => minHeight || '1.4em'};
    min-width: 1px;
`;

export const TableFooter = styled.div`
    align-items: center;
    background: ${white.secondary};
    border-top: 1px solid ${greys.border};
    color: ${greys.primary};
    display: flex;
    font-size: 13px;
    justify-content: space-between;
    padding: 12px 16px;
`;

export const EmptyState = styled.div`
    align-items: center;
    color: ${greys.primary};
    display: flex;
    font-size: 14px;
    justify-content: center;
    padding: 3rem 1rem;
`;
